import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add,
  Search as SearchIcon,
  Refresh,
  Assignment,
  HourglassTop,
  CheckCircle,
  SwapHoriz,
  FilterList,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  TypePill,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';

// ---------------------------------------------------------------------------
// Types -- mirror the raw snake_case rows returned by
// GET /purchase-requisitions (see erp-api src/models/PurchaseRequisition.js
// HEADER_SELECT: pr.* plus joined requested_by_name / cost_center_name /
// project_name / warehouse_name / converted_po_number). The backend's list
// query does not currently project a line-item count, so item_count is kept
// optional and rendered as an em dash when absent rather than assumed.
// ---------------------------------------------------------------------------

export type RequisitionType =
  | 'MATERIAL'
  | 'SERVICE'
  | 'CAPEX'
  | 'SUBCONTRACTING'
  | 'JOB_WORK'
  | 'CONSUMABLE'
  | 'ASSET'
  | 'OTHER';

export type RequisitionPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

export type RequisitionStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'APPROVED'
  | 'CONVERTED_TO_PO'
  | 'REJECTED'
  | 'RETURNED'
  | 'CANCELLED';

export interface PurchaseRequisitionRow {
  id: number;
  requisition_number: string;
  requisition_date: string;
  requisition_type: RequisitionType;
  department: string | null;
  priority: RequisitionPriority;
  status: RequisitionStatus;
  total_estimated_cost: number | string;
  required_by_date: string | null;
  requested_by_name?: string | null;
  item_count?: number;
  [key: string]: any;
}

interface RequisitionSummary {
  byStatus: Record<string, { count: number; value: number }>;
  totalCount: number;
  totalValue: number;
  pendingApprovalCount: number;
  pendingApprovalValue: number;
}

const REQUISITION_TYPES: RequisitionType[] = [
  'MATERIAL',
  'SERVICE',
  'CAPEX',
  'SUBCONTRACTING',
  'JOB_WORK',
  'CONSUMABLE',
  'ASSET',
  'OTHER',
];

const STATUS_OPTIONS: RequisitionStatus[] = [
  'DRAFT',
  'SUBMITTED',
  'UNDER_REVIEW',
  'APPROVED',
  'CONVERTED_TO_PO',
  'REJECTED',
  'RETURNED',
  'CANCELLED',
];

const STATUS_TONE: Record<RequisitionStatus, StatusTone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'info',
  UNDER_REVIEW: 'warning',
  APPROVED: 'success',
  CONVERTED_TO_PO: 'info',
  REJECTED: 'error',
  RETURNED: 'warning',
  CANCELLED: 'neutral',
};

const TYPE_TINT: Record<RequisitionType, string> = {
  MATERIAL: '#0284c7',
  SERVICE: '#7c3aed',
  CAPEX: '#dc2626',
  SUBCONTRACTING: '#d97706',
  JOB_WORK: '#059669',
  CONSUMABLE: '#64748b',
  ASSET: '#0f766e',
  OTHER: '#475569',
};

const PRIORITY_COLOR: Record<RequisitionPriority, 'default' | 'info' | 'warning' | 'error'> = {
  LOW: 'default',
  MEDIUM: 'info',
  HIGH: 'warning',
  URGENT: 'error',
};

const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined) return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const PurchaseRequisitionPage: React.FC = () => {
  const navigate = useNavigate();

  const [requisitions, setRequisitions] = useState<PurchaseRequisitionRow[]>([]);
  const [summary, setSummary] = useState<RequisitionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<RequisitionStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<RequisitionType | ''>('');
  const [search, setSearch] = useState('');

  const fetchAll = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<{ requisitions: PurchaseRequisitionRow[]; count: number }>('/purchase-requisitions', {
        params: {
          status: statusFilter || undefined,
          requisitionType: typeFilter || undefined,
          search: search || undefined,
        },
      }),
      api.get<{ summary: RequisitionSummary }>('/purchase-requisitions/summary'),
    ])
      .then(([reqRes, summaryRes]) => {
        setRequisitions(reqRes.requisitions);
        setSummary(summaryRes.summary);
      })
      .catch((err: any) => setError(err.message || 'Failed to load purchase requisitions'))
      .finally(() => setLoading(false));
  }, [statusFilter, typeFilter, search]);

  // Debounce the free-text search only -- status/type filters refetch immediately.
  useEffect(() => {
    const handle = setTimeout(fetchAll, search ? 350 : 0);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter, search]);

  const pendingCount = summary?.pendingApprovalCount ?? null;
  const pendingValue = summary?.pendingApprovalValue ?? 0;
  const approvedStat = summary?.byStatus?.APPROVED;
  const convertedStat = summary?.byStatus?.CONVERTED_TO_PO;

  const hasFilters = !!(statusFilter || typeFilter || search);
  const clearFilters = () => {
    setStatusFilter('');
    setTypeFilter('');
    setSearch('');
  };

  return (
    <Box>
      <PageHeader
        title="Purchase Requisitions"
        subtitle="Raise, approve and convert internal material / service requests into purchase orders"
        actions={
          <>
            <IconButton onClick={fetchAll} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<Add />}
              size="small"
              onClick={() => navigate('/procurement/requisitions/new')}
            >
              New Requisition
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 2.5,
        }}
      >
        <StatCard
          title="Total Requisitions"
          value={summary ? summary.totalCount : null}
          icon={<Assignment />}
          color="#2563eb"
          footer={
            summary ? (
              <Typography variant="caption" color="text.secondary">
                {formatIndianNumber(summary.totalValue)} estimated
              </Typography>
            ) : undefined
          }
        />
        <StatCard
          title="Pending Approval"
          value={pendingCount}
          icon={<HourglassTop />}
          color="#d97706"
          footer={
            <Typography variant="caption" color="text.secondary">
              {formatIndianNumber(pendingValue)} awaiting a decision
            </Typography>
          }
        />
        <StatCard
          title="Approved"
          value={approvedStat ? approvedStat.count : summary ? 0 : null}
          icon={<CheckCircle />}
          color="#059669"
          footer={
            <Typography variant="caption" color="text.secondary">
              {formatIndianNumber(approvedStat?.value ?? 0)} ready to convert
            </Typography>
          }
        />
        <StatCard
          title="Converted to PO"
          value={convertedStat ? convertedStat.count : summary ? 0 : null}
          icon={<SwapHoriz />}
          color="#7c3aed"
          footer={
            <Typography variant="caption" color="text.secondary">
              {formatIndianNumber(convertedStat?.value ?? 0)} became purchase orders
            </Typography>
          }
        />
      </Box>

      <FilterBar>
        <TextField
          size="small"
          label="Search"
          placeholder="Requisition # or justification"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as RequisitionStatus | '')}
          sx={{ minWidth: 180 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {s.replace(/_/g, ' ')}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Type"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as RequisitionType | '')}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">All Types</MenuItem>
          {REQUISITION_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t.replace(/_/g, ' ')}
            </MenuItem>
          ))}
        </TextField>

        <FilterSpacer />

        {hasFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </FilterBar>

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Requisition', 'Type', 'Department', 'Priority', 'Status', 'Est. Value', 'Required By', ''].map(
                  (h, i) => (
                    <TableCell key={h || i} align={i === 5 ? 'right' : 'left'} sx={headerCellSx}>
                      {h}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : requisitions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ border: 0 }}>
                    <TableEmptyState
                      title="No purchase requisitions match these filters"
                      hint="Clear the filters, or raise a new requisition."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                requisitions.map((req) => (
                  <TableRow
                    key={req.id}
                    sx={{ ...bodyRowSx, cursor: 'pointer' }}
                    hover
                    onClick={() => navigate(`/procurement/requisitions/${req.id}`)}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <CodeCell
                        code={req.requisition_number}
                        tint={TYPE_TINT[req.requisition_type] || '#475569'}
                        icon={<Assignment fontSize="small" />}
                      />
                    </TableCell>
                    <TableCell>
                      <TypePill
                        label={req.requisition_type.replace(/_/g, ' ')}
                        tint={TYPE_TINT[req.requisition_type] || '#475569'}
                      />
                    </TableCell>
                    <TableCell>{req.department || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={req.priority} color={PRIORITY_COLOR[req.priority]} />
                    </TableCell>
                    <TableCell>
                      <StatusDot label={req.status.replace(/_/g, ' ')} tone={STATUS_TONE[req.status]} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                      {formatIndianNumber(toNum(req.total_estimated_cost))}
                    </TableCell>
                    <TableCell>{fmtDate(req.required_by_date)}</TableCell>
                    <TableCell align="right">
                      {req.item_count !== undefined ? (
                        <Typography variant="caption" color="text.secondary">
                          {req.item_count} item{req.item_count === 1 ? '' : 's'}
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>
    </Box>
  );
};

export default PurchaseRequisitionPage;
